import React, { useState, useContext } from "react";
import { View, StyleSheet, ScrollView } from "react-native";
import colors from "../../config/colors";
import NtActivityIndicator from "../../components/NtActivityIndicator";
import UserContext from "../../context/UserContext";
import NtModalUnactiveButton from "../../components/NtModalUnactiveButton";
import NtModalActiveButton from "../../components/NtModalActiveButton";
import NtModalInputField from "../../components/NtModalInputField";
import NtModalAccountPickerField from "./NtModalAccountPickerField";
import NtToggleRow from "../../components/NtToggleRow";
import NtModalError from "../../components/NtModalError";
import NtModalTitle from "../../components/NtModalTitle";
import NtModalSubtitle from "../../components/NtModalSubtitle";
import { createInboundRoute } from "../../api/inboundroute";
import { parseError } from "@ntconnect/framework/pbx";
import Logger from "../../common/utils/Logger";

function NtInboundRouteNewForm({ onCancel, onCreate }) {
  const [name, setName] = useState("");
  const [prefix, setPrefix] = useState("");
  const [destination, setDestination] = useState("");
  const [description, setDescription] = useState("");
  const [action, setAction] = useState("");
  const [callerIdName, setCallerIdName] = useState("");
  const [callerIdNumber, setCallerIdNumber] = useState("");
  const [order, setOrder] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const context = useContext(UserContext);

  const isRequiredFields = () => !!(name && destination && action);

  const handleCreate = async () => {
    setLoading(true);
    setError(null);

    const params = {
      name,
      prefix: prefix || undefined,
      destination,
      description: description || undefined,
      action,
      enabled,
      order: order ? parseInt(order, 10) : undefined,
      callerIdName: callerIdName || undefined,
      callerIdNumber: callerIdNumber || undefined,
    };

    const response = await createInboundRoute(
      context.user?.domain,
      params,
    );

    Logger("Inbound Route Create", response);

    if (response.ok) {
      if (onCreate) onCreate();
    } else {
      setError(parseError(response));
    }
    setLoading(false);
  };

  return (
    <View style={styles.container}>
      <NtModalTitle>Add new Inbound Route</NtModalTitle>
      <NtModalSubtitle style={{ marginTop: 4 }}>
        Route incoming calls to destinations based on one or more conditions. It
        can send incoming calls to an IVR Menu, Ring Group, Extension, External
        Number, etc.
      </NtModalSubtitle>

      <ScrollView
        scrollEnabled={true}
        contentContainerStyle={{ flexGrow: 1 }}
        showsVerticalScrollIndicator={false}
      >
        <NtModalInputField
          containerStyle={{ marginTop: 25 }}
          title="Name *"
          placeholder="Name"
          value={name}
          setValue={setName}
        />

        <View
          style={{ flexDirection: "row", alignItems: "flex-start", marginTop: 15 }}
        >
          <NtModalInputField
            containerStyle={{ flex: 1 }}
            title="Prefix"
            placeholder="Optional prefix"
            value={prefix}
            setValue={(text) => setPrefix(text.replace(/\D/g, ""))}
            keyboardType="numeric"
            footer="Optional digits matched before the destination number."
          />
          <NtModalInputField
            containerStyle={{ flex: 1.5, marginLeft: 10 }}
            title="Destination *"
            placeholder="Destination Phone Number"
            value={destination}
            setValue={(text) => setDestination(text.replace(/\D/g, ""))}
            keyboardType="numeric"
          />
        </View>

        <NtModalInputField
          containerStyle={{ marginTop: 15 }}
          title="Description"
          placeholder="Optional — defaults to auto-generated summary"
          value={description}
          setValue={setDescription}
        />

        <NtModalAccountPickerField
          containerStyle={{ marginTop: 15 }}
          title="Route Call To *"
          placeholder="Route Call To"
          value={action}
          setValue={setAction}
        />

        <View
          style={{ flexDirection: "row", alignItems: "flex-start", marginTop: 15 }}
        >
          <NtModalInputField
            containerStyle={{ flex: 1 }}
            title="Caller ID Name Override"
            placeholder="Optional"
            value={callerIdName}
            setValue={setCallerIdName}
          />
          <NtModalInputField
            containerStyle={{ flex: 1, marginLeft: 10 }}
            title="Caller ID Number Override"
            placeholder="Optional digits"
            value={callerIdNumber}
            setValue={(text) => setCallerIdNumber(text.replace(/\D/g, ""))}
            keyboardType="numeric"
          />
        </View>

        <NtModalInputField
          containerStyle={{ marginTop: 15 }}
          title="Order"
          placeholder="100"
          value={order}
          setValue={(text) => setOrder(text.replace(/\D/g, ""))}
          keyboardType="numeric"
          footer="Rule priority when multiple inbound routes match — lower runs first."
        />

        <View style={{ marginTop: 16 }}>
          <NtToggleRow
            value={enabled}
            onChange={setEnabled}
            label="Enable inbound route"
            sub="When off, calls to this destination won't follow this route."
          />
        </View>

        <NtModalError error={error} containerStyle={{ marginTop: 6 }} />
      </ScrollView>

      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-end",
          marginTop: 25,
        }}
      >
        {loading && (
          <NtActivityIndicator
            color={colors.blue}
            size="small"
            containerStyle={{ marginRight: 25 }}
          />
        )}
        <NtModalUnactiveButton title="Cancel" onPress={onCancel} />
        <NtModalActiveButton
          containerStyle={{ marginLeft: 15 }}
          title="Create"
          onPress={() => {
            if (isRequiredFields()) handleCreate();
          }}
          enabled={isRequiredFields()}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minWidth: 500,
    maxWidth: 600,
    padding: 15,
  },
});

export default NtInboundRouteNewForm;
